/**
 * @fileoverview Loads the pinned Legacy Product presentation catalogs for the migrated common shell.
 */

import playerTitlesJson from "../../../config/platform/player-titles.json" with { type: "json" };
import coinPackagesJson from "../../../config/platform/coin-packages.json" with { type: "json" };
import platformEconomyJson from "../../../config/platform/platform-economy.json" with { type: "json" };
import sharedCosmeticsJson from "../../../config/platform/shared-cosmetics.json" with { type: "json" };
import sharedCardCosmeticsJson from "../../../config/platform/shared-card-cosmetics.json" with { type: "json" };
import dailyRewardsJson from "../../../config/platform/daily-rewards.json" with { type: "json" };
import backgammonJson from "../../../config/games/backgammon.json" with { type: "json" };
import battleshipJson from "../../../config/games/battleship.json" with { type: "json" };
import chaharBargJson from "../../../config/games/chahar-barg.json" with { type: "json" };
import chessJson from "../../../config/games/chess.json" with { type: "json" };
import dabernaJson from "../../../config/games/daberna.json" with { type: "json" };
import dominoJson from "../../../config/games/domino.json" with { type: "json" };
import durakJson from "../../../config/games/durak.json" with { type: "json" };
import eightBallJson from "../../../config/games/eight-ball.json" with { type: "json" };
import hokmJson from "../../../config/games/hokm.json" with { type: "json" };
import ludoJson from "../../../config/games/ludo.json" with { type: "json" };
import marpeleJson from "../../../config/games/marpele.json" with { type: "json" };
import minesweeperJson from "../../../config/games/minesweeper.json" with { type: "json" };
import mafiaJson from "../../../config/games/mafia.json" with { type: "json" };
import noghteKhatJson from "../../../config/games/noghte-khat.json" with { type: "json" };
import othelloJson from "../../../config/games/othello.json" with { type: "json" };
import quizJson from "../../../config/games/quiz.json" with { type: "json" };
import soccerFlickJson from "../../../config/games/soccer-flick.json" with { type: "json" };
import spadesJson from "../../../config/games/spades.json" with { type: "json" };
import sudokuJson from "../../../config/games/sudoku.json" with { type: "json" };
import unoJson from "../../../config/games/uno.json" with { type: "json" };
import xoJson from "../../../config/games/xo.json" with { type: "json" };

const rawGameConfigs = [
  backgammonJson,
  battleshipJson,
  chaharBargJson,
  chessJson,
  dabernaJson,
  dominoJson,
  durakJson,
  eightBallJson,
  hokmJson,
  ludoJson,
  marpeleJson,
  minesweeperJson,
  mafiaJson,
  noghteKhatJson,
  othelloJson,
  quizJson,
  soccerFlickJson,
  spadesJson,
  sudokuJson,
  unoJson,
  xoJson
];
const expectedGameIds = [
  "backgammon", "battleship", "chahar-barg", "chess", "daberna", "domino", "durak",
  "eight-ball", "hokm", "ludo", "marpele", "minesweeper", "mafia", "noghte-khat", "othello", "quiz",
  "soccer-flick", "spades", "sudoku", "uno", "xo"
];
const requiredGameEconomyKeys = [
  "offlineEntryCoins", "offlineWinCoins", "offlineWinXp", "offlineLossXp",
  "onlineEntryCoins", "onlineWinCoins", "onlineWinXp", "onlineLossXp"
];

/** Validated immutable title configuration. */
export const playerTitlesConfig = deepFreeze(validatePlayerTitles(playerTitlesJson));
/** Validated immutable coin-package configuration. */
export const coinPackagesConfig = deepFreeze(validateCoinPackages(coinPackagesJson));
/** Validated immutable shared platform economy configuration. */
export const platformEconomyConfig = deepFreeze(validatePlatformEconomy(platformEconomyJson));
/** Validated immutable shared cosmetic configuration. */
export const sharedCosmeticsConfig = deepFreeze(validateSharedCosmetics(sharedCosmeticsJson));
/** Validated immutable card cosmetic configuration shared by Hokm and Spades. */
export const sharedCardCosmeticsConfig = deepFreeze(validateSharedCardCosmetics(sharedCardCosmeticsJson));
/** Validated immutable 30-day daily reward presentation catalog. */
export const dailyRewardsConfig = deepFreeze(validateDailyRewards(dailyRewardsJson));
/** Validated immutable game configurations keyed by registered game id. */
export const gameConfigs = deepFreeze(validateGameConfigs(rawGameConfigs));

/**
 * Returns the JSON-backed economy record for a game without transferring mutable ownership.
 * @param {string} gameId - Registered Mini Land game identifier.
 * @returns {object} Immutable economy values, with zero-value defaults for games without a dedicated record.
 */
export function gameEconomyFor(gameId) {
  return gameConfigs[String(gameId || "").trim()]?.economy || DEFAULT_GAME_ECONOMY;
}

/**
 * Returns the validated JSON-backed tutorial model for a game.
 * @param {string} gameId - Registered Mini Land game identifier.
 * @returns {object|null} Immutable tutorial metadata or null when the game has no tutorial.
 */
export function gameTutorialFor(gameId) {
  return gameConfigs[String(gameId || "").trim()]?.tutorial || null;
}

/**
 * Adapts a game's authoritative economy record to the shared offline menu renderer.
 * @param {string} gameId - Registered Mini Land game identifier.
 * @returns {{entry:number,prizeXp:number}} Menu display values backed by the game JSON.
 */
export function offlineMenuEconomyFor(gameId) {
  const economy = gameEconomyFor(gameId);
  return { entry: economy.offlineEntryCoins, prizeXp: economy.offlineWinXp };
}

/**
 * Adapts a game's authoritative economy record to the shared online menu renderer.
 * @param {string} gameId - Registered Mini Land game identifier.
 * @returns {{entry:number,prize:null}} Menu display values backed by the game JSON.
 */
export function onlineMenuEconomyFor(gameId) {
  const economy = gameEconomyFor(gameId);
  return { entry: economy.onlineEntryCoins, prize: null };
}

/**
 * Derives the configured global title from the highest level in a per-game stats map.
 * @param {object} [statsMap] - Per-game stats keyed by game id.
 * @returns {{id:string,nameKey:string,highestLevel:number}} Stable title projection safe for owner and public profiles.
 */
export function playerTitleForStats(statsMap = {}) {
  const highestLevel = Math.max(1, ...Object.values(statsMap || {}).map((stats) => integer(stats?.level, 1)));
  const title = [...playerTitlesConfig.titles].reverse().find((item) => highestLevel >= item.minHighestLevel)
    || playerTitlesConfig.titles[0];
  return { id: title.id, nameKey: title.nameKey, highestLevel };
}

const DEFAULT_GAME_ECONOMY = deepFreeze({
  offlineEntryCoins: 0,
  offlineWinCoins: 0,
  offlineWinXp: 20,
  offlineLossXp: 0,
  onlineEntryCoins: 0,
  onlineWinCoins: 0,
  onlineWinXp: 0,
  onlineLossXp: 0
});

function validatePlayerTitles(config) {
  assertVersion(config, "player titles");
  assertCatalog(config.titles, "player titles", { requireNameKey: true });
  const expected = ["rookie", "master", "legend"];
  assert(config.titles.length === expected.length, "player titles must contain Rookie, Master, and Legend");
  let previous = 0;
  config.titles.forEach((title, index) => {
    assert(title.id === expected[index], `player title ${index} must be ${expected[index]}`);
    assertPositiveInteger(title.minHighestLevel, `${title.id}.minHighestLevel`);
    assert(title.minHighestLevel > previous, "player title thresholds must be strictly increasing");
    previous = title.minHighestLevel;
  });
  assert(config.titles[0].minHighestLevel === 1, "Rookie must start at level 1");
  return config;
}

function validateCoinPackages(config) {
  assertVersion(config, "coin packages");
  assertCatalog(config.packages, "coin packages", { requireNameKey: true });
  for (const item of config.packages) {
    assertPositiveInteger(item.coins, `${item.id}.coins`);
    assertPositiveInteger(item.priceIrt, `${item.id}.priceIrt`);
    assertPositiveInteger(item.priceUsdCents, `${item.id}.priceUsdCents`);
  }
  return config;
}

function validatePlatformEconomy(config) {
  assertVersion(config, "platform economy");
  assertNonNegativeInteger(config.initialCoins, "initialCoins");
  assertPositiveInteger(config.rename?.freeChanges, "rename.freeChanges");
  assertPositiveInteger(config.rename?.paidBaseCoins, "rename.paidBaseCoins");
  assert(config.rename?.curve === "triangular", "rename.curve must be triangular");
  assertPositiveInteger(config.progression?.xpBase, "progression.xpBase");
  assertPositiveInteger(config.progression?.xpPerLevel, "progression.xpPerLevel");
  assertNonNegativeInteger(config.progression?.levelUpCoinBonus, "progression.levelUpCoinBonus");
  return config;
}

function validateSharedCosmetics(config) {
  assertVersion(config, "shared cosmetics");
  for (const key of ["avatars", "avatarFrames", "nameStyles"]) {
    assertCatalog(config[key], `shared cosmetics.${key}`, { requireNameKey: true, requireFreeDefault: true });
  }
  for (const [key, ids] of Object.entries(config.alwaysOwned || {})) {
    assert(Array.isArray(ids) && ids.length > 0, `alwaysOwned.${key} must be a non-empty array`);
    assert(Array.isArray(config[key]), `alwaysOwned.${key} must reference a known catalog`);
    const catalogIds = new Set(config[key].map((item) => item.id));
    for (const id of ids) assert(catalogIds.has(id), `alwaysOwned.${key} contains unknown id ${id}`);
  }
  return config;
}

function validateSharedCardCosmetics(config) {
  assertVersion(config, "shared card cosmetics");
  assertCatalog(config.cardBacks, "cardBacks", { requireNameKey: true, requireFreeDefault: true, requireAsset: true, assetKey: "src" });
  assertCatalog(config.cardFronts, "cardFronts", { requireNameKey: true, requireFreeDefault: true });
  return config;
}

function validateDailyRewards(config) {
  assertVersion(config, "daily rewards");
  assert(config.enabled === true && config.timeZone === "UTC", "daily rewards must use the enabled UTC Product policy");
  assert(Array.isArray(config.days) && config.days.length === 30, "daily rewards must contain the canonical 30-day cycle");
  config.days.forEach((day, index) => {
    assert(day.id === `daily-${index + 1}` && day.day === index + 1, `daily reward day ${index + 1} is misaligned`);
    assert(Array.isArray(day.rewards) && day.rewards.length > 0, `daily reward day ${index + 1} must contain rewards`);
  });
  return config;
}

function validateGameConfig(config) {
  assertVersion(config, `game ${config?.gameId || "unknown"}`);
  assertId(config.gameId, "gameId");
  assert(config.economy && typeof config.economy === "object", `${config.gameId}.economy is required`);
  for (const key of requiredGameEconomyKeys) {
    assert(Object.hasOwn(config.economy, key), `${config.gameId}.economy.${key} is required`);
  }
  for (const [key, value] of Object.entries(config.economy)) {
    assertNonNegativeInteger(value, `${config.gameId}.economy.${key}`);
  }
  for (const [key, catalog] of Object.entries(config.cosmetics || {})) {
    if (!Array.isArray(catalog)) continue;
    assertCatalog(catalog, `${config.gameId}.cosmetics.${key}`, {
      requireNameKey: true,
      requireFreeDefault: true,
      requireAsset: catalog.some((item) => item.image),
      assetKey: "image"
    });
  }
  if (config.tutorial !== undefined) validateGameTutorial(config.gameId, config.tutorial);
  return config;
}

/**
 * Validates a game-owned tutorial model before shared UI or future admin readers consume it.
 * @param {string} gameId - Owning game identifier.
 * @param {object} tutorial - Declarative tutorial model from the game JSON.
 * @returns {object} The validated tutorial model.
 */
function validateGameTutorial(gameId, tutorial) {
  assert(tutorial && typeof tutorial === "object" && !Array.isArray(tutorial), `${gameId}.tutorial must be an object`);
  assert(tutorial.view === "rules", `${gameId}.tutorial.view must be rules`);
  assert(typeof tutorial.settingsAccess === "boolean", `${gameId}.tutorial.settingsAccess must be boolean`);
  for (const key of ["navigationLabelKey", "kickerKey", "titleKey", "summaryKey"]) {
    assertTutorialI18nKey(gameId, tutorial[key], `${gameId}.tutorial.${key}`);
  }
  assert(Array.isArray(tutorial.sections) && tutorial.sections.length > 0, `${gameId}.tutorial.sections must be non-empty`);
  const sectionIds = new Set();
  for (const [index, section] of tutorial.sections.entries()) {
    assert(section && typeof section === "object" && !Array.isArray(section), `${gameId}.tutorial.sections.${index} must be an object`);
    assertId(section.id, `${gameId}.tutorial.sections.${index}.id`);
    assert(!sectionIds.has(section.id), `${gameId}.tutorial has duplicate section ${section.id}`);
    sectionIds.add(section.id);
    assertTutorialI18nKey(gameId, section.key, `${gameId}.tutorial.sections.${index}.key`);
  }
  return tutorial;
}

function assertTutorialI18nKey(gameId, value, label) {
  assert(typeof value === "string" && value.startsWith(`${gameId}.rules.`), `${label} must use the ${gameId}.rules namespace`);
}

function validateGameConfigs(configs) {
  assert(configs.length === expectedGameIds.length, "every registered game must have one configuration");
  const ids = new Set();
  const entries = configs.map((config, index) => {
    const validated = validateGameConfig(config);
    assert(validated.gameId === expectedGameIds[index], `${expectedGameIds[index]} configuration has mismatched gameId ${validated.gameId}`);
    assert(!ids.has(validated.gameId), `duplicate game configuration ${validated.gameId}`);
    ids.add(validated.gameId);
    return [validated.gameId, validated];
  });
  return Object.fromEntries(entries);
}

function assertCatalog(items, label, options = {}) {
  assert(Array.isArray(items) && items.length > 0, `${label} must be a non-empty array`);
  const ids = new Set();
  for (const item of items) {
    assertId(item?.id, `${label}.id`);
    assert(!ids.has(item.id), `${label} contains duplicate id ${item.id}`);
    ids.add(item.id);
    if (options.requireNameKey) assertNameKey(item.nameKey, `${label}.${item.id}.nameKey`);
    if (Object.hasOwn(item, "price")) assertNonNegativeInteger(item.price, `${label}.${item.id}.price`);
    if (options.requireAsset) assertAsset(item[options.assetKey || "image"], `${label}.${item.id}.${options.assetKey || "image"}`);
  }
  if (options.requireFreeDefault) assert(items[0].price === 0, `${label} default item must be free`);
}

function assertVersion(config, label) {
  assert(config && typeof config === "object", `${label} configuration is required`);
  assert(config.version === 1, `${label} configuration version must be 1`);
}

function assertId(value, label) {
  assert(/^[a-z0-9][a-z0-9-]*$/.test(String(value || "")), `${label} must be a stable lowercase id`);
}

function assertNameKey(value, label) {
  assert(/^[A-Za-z][A-Za-z0-9]*(?:\.[A-Za-z0-9]+)+$/.test(String(value || "")), `${label} must be an i18n key`);
}

function assertAsset(value, label) {
  assert(String(value || "").startsWith("/assets/"), `${label} must be an /assets path`);
}

function assertPositiveInteger(value, label) {
  assert(Number.isInteger(value) && value > 0, `${label} must be a positive integer`);
}

function assertNonNegativeInteger(value, label) {
  assert(Number.isInteger(value) && value >= 0, `${label} must be a non-negative integer`);
}

function integer(value, fallback = 0) {
  return Number.isFinite(Number(value)) ? Math.floor(Number(value)) : fallback;
}

function assert(condition, message) {
  if (condition) return;
  throw new Error(`Invalid product configuration: ${message}`);
}

function deepFreeze(value) {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) return value;
  Object.freeze(value);
  for (const nested of Object.values(value)) deepFreeze(nested);
  return value;
}
